'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DateInput } from '@/components/ui/date-input'
import { formatDate } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Pencil, Trash2, FolderPlus, Loader2, Users, History, MessageSquare, FolderKanban, TrendingUp, Activity, FolderOpen } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { User } from '@/types/user'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardDescription } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

interface ProjectAssignment {
  user: User | null
  role: string
}

interface ProgressUpdate {
  id: string
  updateText: string
  progressPercentage: number | null
  createdAt: string
  user: {
    id: string
    name: string
    employeeId: string | null
  }
}

interface Project {
  id: string
  name: string
  description: string | null
  status: string
  priority: string
  startDate: string | null
  endDate: string | null
  progress: number
  assignments: ProjectAssignment[]
}

type GlassTone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'
function toGlassTone(t: ReturnType<typeof statusTone>): GlassTone {
  return t === 'neutral' ? 'default' : t
}

function priorityTone(priority: string): 'red' | 'orange' | 'amber' | 'neutral' {
  switch (priority) {
    case 'CRITICAL': return 'red'
    case 'HIGH': return 'orange'
    case 'MEDIUM': return 'amber'
    default: return 'neutral'
  }
}

export function ProjectManagement() {
  const [projects, setProjects] = useState<Project[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editProject, setEditProject] = useState<Project | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    priority: 'MEDIUM',
    startDate: '',
    endDate: '',
    assignedUsers: [] as string[]
  })
  const [submitting, setSubmitting] = useState(false)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [progressHistory, setProgressHistory] = useState<ProgressUpdate[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [updatingProgress, setUpdatingProgress] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const [projectsResult, usersResult] = await Promise.allSettled([
        apiFetch('/api/projects').then(r => r.json()),
        apiFetch('/api/users').then(r => r.json())
      ])

      if (projectsResult.status === 'fulfilled') {
        if (projectsResult.value.projects) setProjects(projectsResult.value.projects)
      } else {
        console.error('Error fetching projects:', projectsResult.reason)
        toast.error(projectsResult.reason?.message || 'Failed to fetch projects')
      }

      if (usersResult.status === 'fulfilled') {
        if (usersResult.value.users) {
          const showInactive = localStorage.getItem('sasha_show_inactive_users') === 'true'
          setUsers(usersResult.value.users.filter((u: User) => u && u.role === 'EMPLOYEE' && (showInactive || u.isActive !== false)))
        }
      } else {
        console.error('Error fetching users:', usersResult.reason)
        toast.error(usersResult.reason?.message || 'Failed to fetch users')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const stats = useMemo(() => {
    const total = projects.length
    const active = projects.filter(p => p.status === 'ACTIVE').length
    const avgProgress = total > 0 ? Math.round(projects.reduce((s, p) => s + (p.progress || 0), 0) / total) : 0
    const assigned = new Set<string>()
    projects.forEach(p => p.assignments.forEach(a => a.user?.id && assigned.add(a.user.id)))
    return { total, active, avgProgress, assigned: assigned.size }
  }, [projects])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const url = editProject ? `/api/projects/${editProject.id}` : '/api/projects'
      const method = editProject ? 'PUT' : 'POST'

      await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      toast.success(editProject ? 'Project updated successfully' : 'Project created successfully')
      fetchData()
      setDialogOpen(false)
      resetForm()
    } catch (e: any) {
      toast.error(e.message || 'Failed to save project')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return

    try {
      await apiFetch(`/api/projects/${projectId}`, { method: 'DELETE' })
      toast.success('Project deleted successfully')
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete project')
    }
  }

  const openEditDialog = (project: Project) => {
    setEditProject(project)
    setFormData({
      name: project.name,
      description: project.description || '',
      status: project.status,
      priority: project.priority,
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      assignedUsers: project.assignments.filter(a => a?.user).map(a => a.user!.id)
    })
    setDialogOpen(true)
  }

  const openHistoryDialog = async (project: Project) => {
    setSelectedProject(project)
    setHistoryDialogOpen(true)
    setLoadingHistory(true)
    try {
      const res = await apiFetch(`/api/projects/${project.id}/progress`)
      const data = await res.json()
      if (data.progressUpdates) {
        setProgressHistory(data.progressUpdates)
      }
    } catch (error) {
      console.error('Error fetching progress history:', error)
      toast.error('Failed to fetch progress history')
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleProgressUpdate = async (projectId: string, progress: number) => {
    setUpdatingProgress(projectId)
    try {
      await apiFetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ progress })
      })
      toast.success(`Progress updated to ${progress}%`)
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to update progress')
    } finally {
      setUpdatingProgress(null)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'ACTIVE',
      priority: 'MEDIUM',
      startDate: '',
      endDate: '',
      assignedUsers: []
    })
    setEditProject(null)
  }

  const addButton = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open)
      if (!open) resetForm()
    }}>
      <DialogTrigger asChild>
        <Button className={PRIMARY_BTN}>
          <FolderPlus className='mr-2 h-4 w-4' />
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-2xl bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10'>
        <DialogHeader>
          <DialogTitle className='text-neutral-900 dark:text-white'>{editProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription className='text-neutral-600 dark:text-white/60'>
            {editProject ? 'Update project details' : 'Create a new project and assign team members'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div className='space-y-2 col-span-2'>
              <Label htmlFor='name' className='text-neutral-800 dark:text-white/80'>Project Name</Label>
              <Input id='name' value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className='space-y-2 col-span-2'>
              <Label htmlFor='description' className='text-neutral-800 dark:text-white/80'>Description</Label>
              <Textarea id='description' value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
            <div className='space-y-2'>
              <Label className='text-neutral-800 dark:text-white/80'>Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='PLANNING'>Planning</SelectItem>
                  <SelectItem value='ACTIVE'>Active</SelectItem>
                  <SelectItem value='ON_HOLD'>On Hold</SelectItem>
                  <SelectItem value='COMPLETED'>Completed</SelectItem>
                  <SelectItem value='CANCELLED'>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label className='text-neutral-800 dark:text-white/80'>Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='LOW'>Low</SelectItem>
                  <SelectItem value='MEDIUM'>Medium</SelectItem>
                  <SelectItem value='HIGH'>High</SelectItem>
                  <SelectItem value='CRITICAL'>Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='startDate' className='text-neutral-800 dark:text-white/80'>Start Date (optional)</Label>
              <DateInput id='startDate' value={formData.startDate} onChange={(value) => setFormData({ ...formData, startDate: value })} placeholder='Select start date' />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='endDate' className='text-neutral-800 dark:text-white/80'>End Date (optional)</Label>
              <DateInput id='endDate' value={formData.endDate} onChange={(value) => setFormData({ ...formData, endDate: value })} placeholder='Select end date' />
            </div>
            <div className='space-y-2 col-span-2'>
              <Label className='text-neutral-800 dark:text-white/80'>Assign Team Members</Label>
              <div className='grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-neutral-50/50 dark:bg-white/[0.02] rounded-xl border border-neutral-200 dark:border-white/10'>
                {users.map((user) => (
                  <label key={user.id} className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={formData.assignedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, assignedUsers: [...formData.assignedUsers, user.id] })
                        } else {
                          setFormData({ ...formData, assignedUsers: formData.assignedUsers.filter(id => id !== user.id) })
                        }
                      }}
                      className='rounded border-neutral-200 dark:border-white/20 bg-neutral-950'
                    />
                    <span className='text-sm text-neutral-800 dark:text-white/80'>{user?.name || 'Unknown'}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type='submit' disabled={submitting} className={PRIMARY_BTN}>
              {submitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  {editProject ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editProject ? 'Update Project' : 'Create Project'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )

  return (
    <PageShell>
      <SectionHeader
        title='Project Management'
        description='Create and manage projects'
        icon={FolderKanban}
        action={addButton}
      />

      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4'>
        <KpiTile label='Total Projects' value={stats.total} icon={FolderKanban} tone='orange' />
        <KpiTile label='Active' value={stats.active} icon={Activity} tone='green' />
        <KpiTile label='Avg Progress' value={stats.avgProgress} icon={TrendingUp} tone='cyan' format={(n) => `${n}%`} />
        <KpiTile label='Assigned Interns' value={stats.assigned} icon={Users} tone='violet' />
      </div>

      {loading ? (
        <GlassCard>
          <TabLoader />
        </GlassCard>
      ) : projects.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={FolderOpen}
            title='No projects found'
            description='Create your first project to start assigning team members and tracking progress.'
          />
        </GlassCard>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4'>
          {projects.map((project) => (
            <GlassCard key={project.id} tone={toGlassTone(statusTone(project.status))} interactive>
              <GlassCardHeader>
                <div className='min-w-0 flex-1'>
                  <GlassCardTitle className='truncate text-base'>{project.name}</GlassCardTitle>
                  {project.description && (
                    <GlassCardDescription className='line-clamp-2 mt-1'>{project.description}</GlassCardDescription>
                  )}
                </div>
                <div className='flex items-center gap-1 shrink-0'>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => openHistoryDialog(project)}
                    className='text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white h-8 w-8'
                    title='View Progress History'
                  >
                    <History className='h-4 w-4' />
                  </Button>
                  <Button variant='ghost' size='icon' onClick={() => openEditDialog(project)} className='text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white h-8 w-8'>
                    <Pencil className='h-4 w-4' />
                  </Button>
                  <Button variant='ghost' size='icon' onClick={() => handleDelete(project.id)} className='text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200 h-8 w-8'>
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              </GlassCardHeader>

              <div className='flex flex-wrap items-center gap-2 mb-3'>
                <StatusPill tone={statusTone(project.status)} dot>{project.status.replace('_', ' ')}</StatusPill>
                <StatusPill tone={priorityTone(project.priority)}>{project.priority}</StatusPill>
              </div>

              <div className='space-y-2 mb-3'>
                <div className='flex items-center justify-between text-xs text-neutral-600 dark:text-white/60'>
                  <span>Progress</span>
                  <span className='tabular-nums text-neutral-900 dark:text-white/90 font-medium'>{project.progress}%</span>
                </div>
                <div className='relative h-2 rounded-full bg-neutral-50 dark:bg-white/5 overflow-hidden ring-1 ring-inset ring-neutral-200 dark:ring-white/10'>
                  <div
                    className='absolute inset-y-0 left-0 bg-gradient-to-r from-orange-400 to-orange-500 transition-[width] duration-500'
                    style={{ width: `${Math.max(0, Math.min(100, project.progress))}%` }}
                  />
                </div>
                <input
                  type='range'
                  min='0'
                  max='100'
                  step='5'
                  value={project.progress}
                  onChange={(e) => {
                    const val = parseInt(e.target.value)
                    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, progress: val } : p))
                  }}
                  onMouseUp={(e) => handleProgressUpdate(project.id, parseInt((e.target as HTMLInputElement).value))}
                  onTouchEnd={(e) => handleProgressUpdate(project.id, parseInt((e.target as HTMLInputElement).value))}
                  className='w-full accent-orange-500 cursor-pointer'
                  disabled={updatingProgress === project.id}
                />
              </div>

              <div className='flex items-center justify-between gap-2 pt-2 border-t border-neutral-100 dark:border-white/5'>
                <div className='flex items-center gap-2 min-w-0'>
                  <div className='flex -space-x-2'>
                    {project.assignments.filter((a) => a?.user).slice(0, 4).map((a) => (
                      <div
                        key={a.user!.id}
                        title={a.user!.name || 'Unknown'}
                        className='h-7 w-7 rounded-full ring-2 ring-neutral-950 bg-gradient-to-br from-orange-500/40 to-violet-500/40 flex items-center justify-center text-[11px] font-medium text-white'
                      >
                        {(a.user!.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    ))}
                    {project.assignments.filter((a) => a?.user).length > 4 && (
                      <div className='h-7 w-7 rounded-full ring-2 ring-neutral-950 bg-white/10 flex items-center justify-center text-[10px] text-neutral-700 dark:text-white/70'>
                        +{project.assignments.filter((a) => a?.user).length - 4}
                      </div>
                    )}
                    {project.assignments.filter((a) => a?.user).length === 0 && (
                      <div className='flex items-center gap-1 text-xs text-neutral-400 dark:text-white/40'>
                        <Users className='h-3.5 w-3.5' />
                        <span>Unassigned</span>
                      </div>
                    )}
                  </div>
                </div>
                {project.endDate && (
                  <span className='text-[11px] text-neutral-400 dark:text-white/40'>Due {formatDate(new Date(project.endDate))}</span>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Progress History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className='max-w-2xl bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10'>
          <DialogHeader>
            <DialogTitle className='text-neutral-900 dark:text-white'>Progress History - {selectedProject?.name}</DialogTitle>
            <DialogDescription className='text-neutral-600 dark:text-white/60'>
              View all progress updates from team members.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className='h-[400px]'>
            <div className='min-w-0'>
              {loadingHistory ? (
                <TabLoader />
              ) : progressHistory.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title='No progress updates yet'
                  description='Updates will appear here when team members submit progress reports.'
                />
              ) : (
                <div className='space-y-3 pr-4'>
                  {progressHistory.map((update) => (
                    <GlassCard key={update.id} inset className='p-4'>
                      <div className='flex items-start justify-between mb-2 gap-2'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <div className='h-8 w-8 rounded-full bg-gradient-to-br from-orange-500/40 to-violet-500/40 flex items-center justify-center text-xs text-white font-medium shrink-0'>
                            {(update.user?.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className='min-w-0'>
                            <p className='text-neutral-900 dark:text-white/90 font-medium truncate'>{update.user?.name || 'Unknown'}</p>
                            <p className='text-xs text-neutral-400 dark:text-white/40 truncate'>{update.user?.employeeId || '-'}</p>
                          </div>
                        </div>
                        <div className='text-right shrink-0'>
                          <p className='text-xs text-neutral-400 dark:text-white/40'>
                            {formatDate(new Date(update.createdAt))}
                          </p>
                          {update.progressPercentage !== null && update.progressPercentage > 0 && (
                            <StatusPill tone='orange' className='mt-1'>
                              {update.progressPercentage}% progress
                            </StatusPill>
                          )}
                        </div>
                      </div>
                      <p className='text-neutral-800 dark:text-white/80 text-sm whitespace-pre-wrap'>{update.updateText}</p>
                    </GlassCard>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </PageShell>
  )
}
